import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useViewerStore } from '@/state/useViewerStore';
import { createScaleProjector, resolveMapRoot } from './scaleClientProjection';
import {
  scaleGeometrySvgPath,
} from './scaleGeometrySvgPath';
import {
  getAllVectorScaleEdits,
  useVectorScaleSessionVersion,
} from './vectorScaleSession';
import {
  getVectorSessionFeatures,
  useVectorEditSessionVersion,
} from './vectorEditSession';

/** Persistent, session-only replacements. No handles: these look like source data. */
export function VectorScaleSessionOverlay({
  container,
}: {
  readonly container?: HTMLElement | null;
}) {
  useVectorScaleSessionVersion();
  useVectorEditSessionVersion();
  const camera = useViewerStore((state) => state.camera);
  const mapRoot = resolveMapRoot(container);
  const projector = useMemo(
    () => createScaleProjector(mapRoot, camera),
    [camera, mapRoot],
  );
  if (typeof document === 'undefined') return null;
  const mapRect = mapRoot?.getBoundingClientRect();
  if (!mapRect) return null;

  const sessionFeatures = getVectorSessionFeatures();
  const sessionKeys = new Set(
    sessionFeatures.map((edit) => `${edit.layerId}:${String(edit.feature.id)}`),
  );
  const scaleEntries = getAllVectorScaleEdits()
    .filter((edit) => !sessionKeys.has(`${edit.layerId}:${String(edit.featureId)}`))
    .map((edit) => ({
    key: `scale:${edit.layerId}:${String(edit.featureId)}`,
    geometry: edit.feature.geometry,
    strokeColor: edit.strokeColor,
    strokeWidth: edit.strokeWidth,
    fillColor: edit.fillColor,
    fillOpacity: edit.fillOpacity,
    }));
  const editEntries = sessionFeatures.map((edit) => ({
    key: `edit:${edit.layerId}:${String(edit.feature.id)}`,
    geometry: edit.feature.geometry,
    strokeColor: edit.strokeColor,
    strokeWidth: edit.strokeWidth,
    fillColor: edit.fillColor,
    fillOpacity: edit.fillOpacity,
  }));
  const paths = [...scaleEntries, ...editEntries]
    .map((edit) => ({
      ...edit,
      path: scaleGeometrySvgPath(edit.geometry, projector),
    }))
    .filter(({ path }) => path.length > 0);
  if (paths.length === 0) return null;

  return createPortal(
    <svg
      data-testid="vector-scale-session-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 39,
      }}
    >
      <defs>
        <clipPath id="vector-scale-map-clip">
          <rect
            x={mapRect.left}
            y={mapRect.top}
            width={mapRect.width}
            height={mapRect.height}
          />
        </clipPath>
      </defs>
      <g clipPath="url(#vector-scale-map-clip)">
        {paths.map(({ key, path, fillColor, fillOpacity, strokeColor, strokeWidth }) => (
          <path
            key={key}
            d={path}
            fill={fillColor}
            fillOpacity={fillOpacity}
            fillRule="evenodd"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </g>
    </svg>,
    document.body,
  );
}
